import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, FilePenLine, Trash2, Filter, AArrowUp, AArrowDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuCheckboxItem } from "../../components/ui/dropdown-menu";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import CatalogItemDialogForm from "../../components/shared/CatalogItemDialogForm";
import { formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { createDropShip, listAllDropShip, updateDropShip, updateDropShipStatus } from "../../reducers/shipdirect.slice";
import { AuthContext } from "../../context/authContext";
import useMediaQuery from "../../components/shared/useMediaQuery";

const SD_STATUSES = ["ACTIVE", "INACTIVE"];

const ShipDirectPage = () => {
  const [openDeleteDropShip, setOpenDeleteDropShip] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [imgPreviewOpen, setImgPreviewOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [nameExpanded, setNameExpanded] = useState(false);
  const [dropShipDetails, setDropShipDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortDirection, setSortDirection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllDropShip = () => {
    setShowLoader(true);
    dispatch(listAllDropShip()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        buyPrice: formatNumberToDollar(item?.buy_price),
        cashoutPrice: formatNumberToDollar(item?.cashout_price),
      }));
      setShowLoader(false);
      setDropShipDetails(formatted);
    });
  };

  useEffect(() => { getAllDropShip(); }, [isRefresh]);

  const filterData = () => {
    let filtered = [...dropShipDetails];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.upc && String(item.upc).toLowerCase().includes(t)) ||
          (item.retailer_name && item.retailer_name.toLowerCase().includes(t)) ||
          (item.item_name && item.item_name.toLowerCase().includes(t))
      );
    }
    if (selectedStatuses.length > 0) filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    if (sortDirection === "asc") filtered.sort((a, b) => Number(a?.total_quantity || 0) - Number(b?.total_quantity || 0));
    else if (sortDirection === "desc") filtered.sort((a, b) => Number(b?.total_quantity || 0) - Number(a?.total_quantity || 0));
    setFilteredData(filtered);
    setCurrentPage(0);
  };

  useEffect(() => { filterData(); }, [dropShipDetails, selectedStatuses, sortDirection, searchTerm]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const handlePressDelete = (row) => { setSelectedRow(row); setOpenDeleteDropShip(true); };

  const handleDeleteShipDirect = () => {
    setShowLoader(true);
    dispatch(updateDropShipStatus({ id: selectedRow.id, status: "INACTIVE" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { setShowLoader(false); showToast(res?.error?.message, "error"); return; }
      setIsRefresh(!isRefresh);
      setShowLoader(false);
      setOpenDeleteDropShip(false);
    });
  };

  const sdStats = useMemo(() => {
    const active = dropShipDetails.filter((d) => d.status === "ACTIVE").length;
    const inactive = dropShipDetails.filter((d) => d.status === "INACTIVE").length;
    return { active, inactive, total: dropShipDetails.length };
  }, [dropShipDetails]);

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      width: 210,
      maxWidth: 210,
      render: (row) => <span style={{ color: "#fff", fontWeight: 500 }}>{row?.item_name}</span>,
    },
    { key: "upc", label: "UPC", width: 130 },
    { key: "retailer_name", label: "Retailer Name", width: 140 },
    { key: "total_quantity", label: "Qty", width: 64, align: "center" },
    { key: "buyPrice", label: "Buy Price", width: 90, align: "right" },
    { key: "cashoutPrice", label: "Cashout Price", width: 110, align: "right" },
    {
      key: "status",
      label: "Status",
      width: 96,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 110,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="ghost" size="icon-sm" title="View" onClick={(e) => { e.stopPropagation(); setImgError(false); setNameExpanded(false); setDetailRow(row); }}>
            <Eye size={15} />
          </Button>
          <Button variant="ghost" size="icon-sm" title="Edit" onClick={() => setEditData(row)}>
            <FilePenLine size={15} />
          </Button>
          {row?.status === "ACTIVE" && (
            <Button variant="ghost" size="icon-sm" title="Delete" onClick={() => handlePressDelete(row)}>
              <Trash2 size={15} style={{ color: "#f27059" }} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <PageHeader
            title="Ship Direct"
            subtitle="Manage Ship Direct inventory directly fulfilled from suppliers to customers."
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
            gap: isMobile ? 12 : 16,
            marginBottom: 20,
          }}
        >
          <div style={statStyle("#4caf50")}>
            <span style={statLabelStyle}>ACTIVE</span>
            <span style={statValueStyle}>{sdStats.active}</span>
          </div>
          <div style={statStyle("#f27059")}>
            <span style={statLabelStyle}>INACTIVE</span>
            <span style={statValueStyle}>{sdStats.inactive}</span>
          </div>
          <div style={statStyle("#00ADB5")}>
            <span style={statLabelStyle}>TOTAL</span>
            <span style={statValueStyle}>{sdStats.total}</span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? 10 : 12,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {!isMobile && <RefreshButton onRefresh={getAllDropShip} spinning={showLoader} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
              <SearchBar
                placeholder="Search by UPC, retailer, name…"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                  <Filter size={14} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {SD_STATUSES.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedStatuses.includes(s)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(s, checked)}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
                  <AArrowUp size={14} style={{ marginRight: 8 }} /> Sort Qty (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
                  <AArrowDown size={14} style={{ marginRight: 8 }} /> Sort Qty (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setCreateOpen(true)} style={{ width: isMobile ? "100%" : "auto" }}>
              Add New Ship Direct Item
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={paginatedRows}
          totalRows={filteredData.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(n) => setCurrentPage(n)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
          loading={showLoader}
          minWidth={940}
        />
      </div>

      {/* Detail dialog */}
      <Dialog open={Boolean(detailRow)} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent
          style={{
            maxWidth: 560,
            width: isMobile ? "calc(100vw - 16px)" : "min(560px, calc(100vw - 64px))",
            background: "#111",
            padding: isMobile ? 16 : 24,
          }}
        >
          <DialogHeader>
            <DialogTitle>Ship Direct Details</DialogTitle>
            <DialogDescription>View details for this Ship Direct item.</DialogDescription>
          </DialogHeader>
          {detailRow && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  gap: 12,
                }}
              >
                <p
                  onClick={() => setNameExpanded(!nameExpanded)}
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#fff",
                    margin: 0,
                    flex: 1,
                    cursor: "pointer",
                    display: "-webkit-box",
                    WebkitLineClamp: nameExpanded ? "unset" : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: nameExpanded ? "visible" : "hidden",
                  }}
                >
                  {detailRow?.item_name}
                </p>
                {imgError || !detailRow?.item_image_url ? (
                  <div
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#8b9296",
                      fontSize: 11,
                      textAlign: "center",
                      padding: 8,
                    }}
                  >
                    Image unavailable
                  </div>
                ) : (
                  <img
                    src={detailRow.item_image_url}
                    alt={detailRow.item_name}
                    onError={() => setImgError(true)}
                    onClick={() => setImgPreviewOpen(true)}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "contain",
                      background: "#fff",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>

              {[
                { label: "UPC", value: detailRow?.upc },
                { label: "Retailer", value: detailRow?.retailer_name },
                { label: "Buy Price", value: detailRow?.buyPrice },
                { label: "Cashout Price", value: detailRow?.cashoutPrice },
                { label: "Total Quantity", value: detailRow?.total_quantity },
                { label: "Max Per User", value: detailRow?.max_per_user_quantity },
                { label: "Status", value: detailRow?.status ? <StatusBadge status={detailRow.status} /> : null },
                { label: "Tags", value: detailRow?.tags },
                { label: "Description", value: detailRow?.description },
                {
                  label: "Item Link",
                  value: detailRow?.item_link ? (
                    <a href={detailRow.item_link} target="_blank" rel="noopener noreferrer" style={{ color: "#00ADB5", textDecoration: "none", fontSize: 13 }}>
                      View item →
                    </a>
                  ) : null,
                },
              ]
                .filter((item) => item.value !== null && item.value !== undefined && item.value !== "")
                .map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      padding: "9px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 10, color: "#8b9296", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 800 }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: "#fff", textAlign: "right" }}>{item.value}</span>
                  </div>
                ))}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const row = detailRow;
                    setDetailRow(null);
                    setEditData(row);
                  }}
                >
                  <FilePenLine size={14} style={{ marginRight: 6 }} /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={imgPreviewOpen} onOpenChange={setImgPreviewOpen}>
        <DialogContent style={{ maxWidth: 480, background: "#111", padding: 16 }}>
          <DialogHeader>
            <DialogTitle>Item Image</DialogTitle>
            <DialogDescription>{detailRow?.item_name}</DialogDescription>
          </DialogHeader>
          <img
            src={detailRow?.item_image_url}
            alt={detailRow?.item_name}
            style={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 400 }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={openDeleteDropShip} onOpenChange={setOpenDeleteDropShip}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Soft Delete</DialogTitle>
            <DialogDescription>
              You are about to delete this Ship Direct Item. Once done, the item will not be visible for members. Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteDropShip(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteShipDirect}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Add Ship Direct</DialogTitle>
            <DialogDescription>Create a new Ship Direct item shipped directly from supplier to customer.</DialogDescription>
          </DialogHeader>
          {createOpen && (
            <CatalogItemDialogForm
              actionCreator={createDropShip}
              showToast={showToast}
              onSuccess={() => { setCreateOpen(false); getAllDropShip(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editData)} onOpenChange={(open) => !open && setEditData(null)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Edit Ship Direct</DialogTitle>
            <DialogDescription>Update the Ship Direct item details below.</DialogDescription>
          </DialogHeader>
          {editData && (
            <CatalogItemDialogForm
              actionCreator={updateDropShip}
              editData={editData}
              mode="edit"
              showToast={showToast}
              submitLabel="Update"
              submittingLabel="Updating..."
              onSuccess={() => { setEditData(null); getAllDropShip(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

export default ShipDirectPage;
